import React, { useState, useEffect } from 'react';
import { telegramApi } from '../services/api';
import { 
  Send, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  MessageSquare,
  Settings,
  HelpCircle
} from 'lucide-react';
import './TelegramSettings.css';

const TelegramSettings = () => {
  const [config, setConfig] = useState({ chat_id: '', topic_id: '', bot_token: '' });
  const [botInfo, setBotInfo] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [hasCustomToken, setHasCustomToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const data = await telegramApi.getConfig();
      setConfig({
        chat_id: data.config?.chat_id || '',
        topic_id: data.config?.topic_id || '',
        // Don't load the actual token into the field for security
        // Just show placeholder if one exists
        bot_token: ''
      });
      setBotInfo(data.bot_info);
      setIsConfigured(data.is_configured);
      // Store whether a custom token exists
      setHasCustomToken(data.config?.has_custom_token || false);
    } catch (error) {
      console.error('Failed to load Telegram config:', error);
      setStatus({ type: 'error', message: 'Failed to load configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.chat_id.trim()) {
      setStatus({ type: 'error', message: 'Chat ID is required' });
      return;
    }
    
    setSaving(true);
    setStatus({ type: '', message: '' });
    
    try {
      // Send empty string for topic_id if user cleared it (to explicitly remove it)
      // Send null for bot_token if empty (to keep existing)
      await telegramApi.updateConfig(
        config.chat_id.trim(), 
        config.topic_id?.trim() ?? "",  // Empty string = clear, value = set
        config.bot_token?.trim() || null  // Empty = keep existing
      );
      setIsConfigured(true);
      setStatus({ type: 'success', message: 'Configuration saved successfully!' });
      // Reload to get updated bot info
      loadConfig();
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setStatus({ type: '', message: '' });
    
    try {
      await telegramApi.testConnection();
      setStatus({ type: 'success', message: 'Test message sent! Check your Telegram group.' });
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to send test message. Make sure the bot is added to your group as an admin.' });
    } finally {
      setTesting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!testMessage.trim()) return;
    
    setSendingMessage(true);
    setStatus({ type: '', message: '' });
    
    try {
      await telegramApi.sendMessage(testMessage.trim());
      setStatus({ type: 'success', message: 'Message sent to Telegram!' });
      setTestMessage('');
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to send message' });
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="telegram-settings-loading">
        <Loader2 size={32} className="animate-spin" />
        <p>Loading Telegram settings...</p>
      </div>
    );
  }

  return (
    <div className="telegram-settings">
      {/* Bot Info */}
      <div className="telegram-section">
        <h3 className="section-title">
          <Send size={18} />
          Bot Information
        </h3>
        <div className="bot-info-card">
          {botInfo ? (
            <>
              <div className="bot-avatar">
                <Send size={24} />
              </div>
              <div className="bot-details">
                <span className="bot-name">{botInfo.first_name}</span>
                <span className="bot-username">@{botInfo.username}</span>
              </div>
              <div className="bot-status connected">
                <CheckCircle size={16} />
                Connected
              </div>
            </>
          ) : (
            <div className="bot-status error">
              <AlertCircle size={16} />
              Bot not connected
            </div>
          )}
        </div>
      </div>

      {/* Configuration */}
      <div className="telegram-section">
        <div className="section-header">
          <h3 className="section-title">
            <Settings size={18} />
            Group Configuration
          </h3>
          <button 
            className="help-toggle"
            onClick={() => setShowHelp(!showHelp)}
          >
            <HelpCircle size={16} />
            {showHelp ? 'Hide Help' : 'Setup Help'}
          </button>
        </div>
        
        {showHelp && (
          <div className="help-box">
            <h4>How to get your Chat ID:</h4>
            <ol>
              <li>Add <strong>@{botInfo?.username || 'CoachCJbot'}</strong> to your Telegram group</li>
              <li>Make the bot an <strong>admin</strong> in the group</li>
              <li>Send any message in the group</li>
              <li>Forward that message to <strong>@userinfobot</strong></li>
              <li>Copy the Chat ID (starts with -100 for groups)</li>
              <li>If your group has topics, also get the Topic ID for the specific topic you want notifications in</li>
            </ol>
          </div>
        )}

        <div className="config-form">
          <div className="form-group">
            <label>Bot Token {hasCustomToken ? '(configured)' : '*'}</label>
            <input
              type="password"
              value={config.bot_token}
              onChange={(e) => setConfig({ ...config, bot_token: e.target.value })}
              placeholder={hasCustomToken ? "••••••••• (leave blank to keep current)" : "123456789:ABCdefGHI..."}
            />
            <span className="form-hint">
              {hasCustomToken 
                ? "Token is saved. Only enter a new value if you want to change it." 
                : "Get this from @BotFather on Telegram. Required for sending messages."}
            </span>
          </div>
          
          <div className="form-group">
            <label>Chat ID *</label>
            <input
              type="text"
              value={config.chat_id}
              onChange={(e) => setConfig({ ...config, chat_id: e.target.value })}
              placeholder="-1001234567890"
            />
            <span className="form-hint">The ID of your Telegram group</span>
          </div>
          
          <div className="form-group">
            <label>Topic ID (Optional)</label>
            <input
              type="text"
              value={config.topic_id}
              onChange={(e) => setConfig({ ...config, topic_id: e.target.value })}
              placeholder="123"
            />
            <span className="form-hint">If your group has topics, specify the topic for notifications</span>
          </div>

          <div className="form-actions">
            <button 
              className="btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
              Save Configuration
            </button>
            
            {isConfigured && (
              <button 
                className="btn-test"
                onClick={handleTest}
                disabled={testing}
              >
                {testing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Send Test Message
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Send Custom Message */}
      {isConfigured && (
        <div className="telegram-section">
          <h3 className="section-title">
            <MessageSquare size={18} />
            Send Custom Message
          </h3>
          <div className="message-form">
            <textarea
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Type a message to send to your Telegram group..."
              rows={3}
            />
            <button
              className="btn-send"
              onClick={handleSendMessage}
              disabled={sendingMessage || !testMessage.trim()}
            >
              {sendingMessage ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Send to Telegram
            </button>
          </div>
        </div>
      )}

      {/* Notifications Info */}
      <div className="telegram-section">
        <h3 className="section-title">
          <AlertCircle size={18} />
          Automatic Notifications
        </h3>
        <div className="notifications-info">
          <p>When configured, Coach CJ will automatically send notifications to your Telegram group for:</p>
          <ul>
            <li>🟢 <strong>Live Stream Alerts</strong> - When you go live, a notification is sent to Telegram</li>
            <li>🎂 <strong>Birthday Messages</strong> - Daily birthday wishes at 8:00am ET</li>
            <li>⚠️ <strong>Canceled Session Reminders</strong> - Morning reminder at 8:15am ET when a session is canceled</li>
          </ul>
        </div>
      </div>

      {/* Status Message */}
      {status.message && (
        <div className={`status-message ${status.type}`}>
          {status.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {status.message}
        </div>
      )}
    </div>
  );
};

export default TelegramSettings;
